/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnInit, ChangeDetectorRef, Component, Injector, Input, ViewChild } from '@angular/core';
import { type ColumnDef, type TableConfig, DataTableComponent, SlideWindowAbstract, UNPLACEHOLDER } from '@dm/core';
import { type OnAccessPolicy, type OnDemandPolicy } from 'app/common/types/mo';

import { AntivirusStrService, ExcludeFileE } from '../../antivirus-str.service';

@Component({
    selector: 'app-type-path-detail-table',
    templateUrl: './type-path-detail-table.component.html',
    styleUrls: ['./type-path-detail-table.component.scss'],
})
export class TypePathDetailTableComponent extends SlideWindowAbstract implements OnInit, AfterViewInit {
    @Input() tableType: ExcludeFileE;
    /**
     * 传入的表格ID
     */
    @Input() tableId: string;
    /**
     * 从“创建命名空间扫描任务”页面进入，需要展示侧滑框
     */
    @Input() isSlideWindow = false;
    /**
     * 只有侧滑框需要传入
     */
    @Input() selectPolicy: OnDemandPolicy | OnAccessPolicy;
    @ViewChild('dt') dt: DataTableComponent;

    i18n = this.str.i18n;
    /**
     * 表格配置
     */
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    tableData: { type: string }[] | { path: string }[] = [];
    /**
     * 侧滑框标题
     */
    title: string;

    constructor(
        injector: Injector,
        public str: AntivirusStrService,
        private cdref: ChangeDetectorRef,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.initTable();

        if (this.isSlideWindow) {
            this.title = this.tableType === ExcludeFileE.PATH ? this.i18n.excludeFilePath : this.i18n.unscannedFileType;
            this.pageReady = true;
            this.windowVisible = true;
        }
    }

    ngAfterViewInit(): void {
        // 侧滑框只能在视图更新后才能获取到dt
        if (this.isSlideWindow) {
            this.handleTableData(this.selectPolicy);
            this.cdref.detectChanges();
        }
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: this.tableType,
        };

        this.tableColumnDefs = [
            {
                header: this.tableType === ExcludeFileE.TYPE ? this.i18n.unscannedFileType : this.i18n.excludeFilePath,
                field: this.tableType,
                filterType: 'search',
                sortable: true,
                filterTrim: false,
            },
        ];
    }

    handleTableData(data: OnAccessPolicy | OnDemandPolicy): void {
        const dataKey = `excludeFile${this.tableType}`;
        if (!data || data[dataKey] === UNPLACEHOLDER) {
            this.dt?.updateData([], false);
            return;
        }

        const dataArr = this.str.excludeFilePathMesRender(data[dataKey]);
        if (dataArr.length) {
            const assembleArr = [];
            dataArr.forEach((item: string) => {
                assembleArr.push({ [this.tableType]: item });
            });
            this.dt?.updateData([...assembleArr], false);
        }
    }

    resetTableData(): void {
        this.tableData = [];
        this.dt?.resetFilters();
    }
}
